import { createStore } from "vuex";
import TokenService from "@services/tokenService";
import route from "@router";
import { jwtDecode } from "jwt-decode";

export const packageVersion = require("@/../package.json").version;

export default createStore({
	state: {
		currentUser: getSavedState("auth.currentUser"),
		userInformation: getSavedState("userInformation"),
	},
	mutations: {
		SET_CURRENT_USER(state, newValue) {
			state.currentUser = newValue;
			saveState("auth.currentUser", newValue);
		},
		SET_USER_INFO(state, newValue) {
			if (newValue && newValue.jwt) {
				const decoded = jwtDecode(newValue.jwt);
				const value = {
					userId: decoded.userId,
					firstName: decoded.firstName,
					lastName: decoded.lastName,
					login: decoded.login,
					role: decoded.role,
					resetPassword: decoded.resetPassword,
					viewablePages: decoded.viewablePages,
				};
				state.userInformation = value;
				window.localStorage.setItem("refreshToken", newValue.refreshToken);
				saveState("userInformation", value);
			} else {
				removeState("userInformation");
			}
		},
		ERASE_RESET_PWD(state, newValue) {
			state.userInformation.resetPassword = newValue;
			saveState("userInformation", state.userInformation);
		},
		ERASE_USER_INFORMATION(state) {
			state.userInformation = undefined;
			removeState("userInformation");
		},
		SET_TITLE(state, newValue) {
			state.title = newValue;
		},
	},
	getters: {
		// Whether the user is currently logged in.
		loggedIn(state) {
			return !!state.currentUser;
		},
		userId(state) {
			return state.userInformation.userId;
		},
		title(state) {
			return state.title;
		},
		viewablePages(state) {
			return state.userInformation?.viewablePages || [];
		},
	},
	actions: {
		// This is automatically run in `src/state/store.js` when the app
		// starts, along with any other actions named `init` in other modules.
		init({ dispatch }) {
			dispatch("validate");
		},
		eraseResetPwd({ commit }) {
			commit("ERASE_RESET_PWD", false);
		},
		eraseUserInformation({ commit }) {
			commit("ERASE_USER_INFORMATION");
		},
		// Logs in the current user.
		logIn({ commit, dispatch, getters }, { login, password } = {}) {
			if (getters.loggedIn) return dispatch("validate");
			var user = { login: login, password: password };
			return TokenService.login(user).then((data) => {
				const user = data;
				commit("SET_CURRENT_USER", user);
				commit("SET_USER_INFO", user);
				return user;
			});
		},
		refresh({ commit, dispatch, getters, state }) {
			if (null != state.refreshPromise) return state.refreshPromise;

			var refreshToken = {
				refreshToken: window.localStorage.getItem("refreshToken"),
				login: state.currentUser.login,
			};
			state.refreshPromise = TokenService.refresh(refreshToken)
				.then((data) => {
					state.refreshPromise = null;
					const user = data;
					commit("SET_CURRENT_USER", user);
					commit("SET_USER_INFO", user);
					return user;
				})
				.finally(() => {
					state.refreshPromise = null;
				});

			return state.refreshPromise;
		},

		// Logs out the current user.
		logOut({ commit }) {
			commit("SET_CURRENT_USER", null);
			commit("SET_USER_INFO", null);
			route.push({ name: "login" });
		},

		logOutSilent({ commit }) {
			commit("SET_CURRENT_USER", null);
			commit("SET_USER_INFO", null);
		},

		logOutExpired({ commit }) {
			commit("SET_CURRENT_USER", null);
			commit("SET_USER_INFO", null);
			route.push({
				name: "login",
				query: { redirectFrom: route.currentRoute.value.fullPath },
			});
		},

		// Validates the current user's token and refreshes it
		// with new data from the API.
		validate({ state }) {
			if (!state.currentUser) return Promise.resolve(null);
			return state.currentUser;
		},
	},
	modules: {},
});

// ===
// Private helpers
// ===

function getSavedState(key) {
	if (process.env.NODE_ENV !== "production") {
		key = import.meta.env.VUE_APP_PROJECT_NAME + "_" + key;
	}
	return JSON.parse(window.localStorage.getItem(key));
}

function saveState(key, state) {
	if (process.env.NODE_ENV !== "production") {
		key = import.meta.env.VUE_APP_PROJECT_NAME + "_" + key;
	}
	window.localStorage.setItem(key, JSON.stringify(state));
}

function removeState(key) {
	if (process.env.NODE_ENV !== "production") {
		key = import.meta.env.VUE_APP_PROJECT_NAME + "_" + key;
	}
	window.localStorage.removeItem(key);
}
